/**
 * Shared Blossom (BUD-02) image upload logic.
 * Used by both useUploadFile (the ImageField UI hook) and SimpleAdapter.uploadImage.
 */

import type { Signer } from '@/lib/signer';

const BLOSSOM_SERVERS = [
  'https://blossom.ditto.pub/',
  'https://blossom.primal.net/',
];

export interface BlossomUploadOptions {
  signal?: AbortSignal;
}

/**
 * Sign a BUD-02 auth event and PUT the file to Blossom servers.
 * Returns the uploaded file's URL. Throws if all servers fail.
 */
export async function uploadToBlossom(
  signer: Signer,
  file: File,
  options?: BlossomUploadOptions,
): Promise<string> {
  options?.signal?.throwIfAborted();

  // Create BUD-02 auth event
  const now = Math.floor(Date.now() / 1000);
  const hashBuffer = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  const hashHex = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  options?.signal?.throwIfAborted();

  const authTemplate = {
    kind: 24242,
    content: 'Upload via Tile Studio',
    created_at: now,
    pubkey: await signer.getPublicKey(),
    tags: [
      ['t', 'upload'],
      ['x', hashHex],
      ['expiration', String(now + 300)],
    ],
  };

  options?.signal?.throwIfAborted();

  const signed = await signer.signEvent(authTemplate);
  const token = btoa(JSON.stringify(signed));

  options?.signal?.throwIfAborted();

  // Try each server, use first success
  let lastError: Error | null = null;
  for (const server of BLOSSOM_SERVERS) {
    options?.signal?.throwIfAborted();

    try {
      const controller = new AbortController();
      const onExternalAbort = () => controller.abort();
      options?.signal?.addEventListener('abort', onExternalAbort, { once: true });

      const timeout = setTimeout(() => controller.abort(), 30_000);

      const res = await fetch(`${server}upload`, {
        method: 'PUT',
        headers: {
          'Authorization': `Nostr ${token}`,
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
        signal: controller.signal,
      });

      clearTimeout(timeout);
      options?.signal?.removeEventListener('abort', onExternalAbort);

      if (!res.ok) {
        lastError = new Error(`Upload to ${server} failed: ${res.status}`);
        continue;
      }

      const data = (await res.json()) as { url: string };
      return data.url;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
    }
  }

  throw lastError ?? new Error('All upload servers failed');
}
